import React, { useEffect, useRef, useState } from 'react';

const SquarePayment = ({ applicationId, locationId, price = 5.00, onPaymentSuccess, onPaymentError }) => {
  const paymentsRef = useRef(null);
  const cardRef = useRef(null);
  const applePayRef = useRef(null);
  const googlePayRef = useRef(null);
  const [applePayAvailable, setApplePayAvailable] = useState(false);
  const [googlePayAvailable, setGooglePayAvailable] = useState(false);

  useEffect(() => {
    const initializeSquare = async () => {
      if (!window.Square) {
        console.error('Square.js failed to load');
        onPaymentError?.('Payment system not available. Please refresh the page.');
        return;
      }

      try {
        console.log('Initializing Square with:', { applicationId, locationId });
        paymentsRef.current = window.Square.payments(applicationId, locationId);

        // Initialize Card payment
        cardRef.current = await paymentsRef.current.card();
        await cardRef.current.attach('#card-container');
        console.log('Square card payment initialized successfully');

        // Initialize Apple Pay and Google Pay if available
        const paymentRequest = paymentsRef.current.paymentRequest({
          countryCode: 'US',
          currencyCode: 'USD',
          total: {
            amount: price.toString(),
            label: 'Emoji Postcard',
          },
        });

        // Try to initialize Apple Pay
        try {
          applePayRef.current = await paymentsRef.current.applePay(paymentRequest);
          setApplePayAvailable(true);
          console.log('Apple Pay initialized successfully');
        } catch (error) {
          console.log('Apple Pay not available:', error.message);
          setApplePayAvailable(false);
        }

        // Try to initialize Google Pay
        try {
          googlePayRef.current = await paymentsRef.current.googlePay(paymentRequest);
          await googlePayRef.current.attach('#google-pay-button');
          setGooglePayAvailable(true);
          console.log('Google Pay initialized successfully');
        } catch (error) {
          console.log('Google Pay not available:', error.message);
          setGooglePayAvailable(false);
        }
      } catch (error) {
        console.error('Failed to initialize Square payment:', error);
        console.error('Error details:', error.message);
        onPaymentError?.(`Failed to load payment form: ${error.message}`);
      }
    };

    // Wait for next tick to ensure component has rendered
    const timer = setTimeout(initializeSquare, 0);

    return () => {
      clearTimeout(timer);
      if (cardRef.current) {
        try {
          cardRef.current.destroy();
        } catch (error) {
          console.error('Error destroying card:', error);
        }
      }
      if (applePayRef.current) {
        try {
          applePayRef.current.destroy();
        } catch (error) {
          console.error('Error destroying Apple Pay:', error);
        }
      }
      if (googlePayRef.current) {
        try {
          googlePayRef.current.destroy();
        } catch (error) {
          console.error('Error destroying Google Pay:', error);
        }
      }
    };
  }, [applicationId, locationId, price, onPaymentError]);

  const handleCardPayment = async (e) => {
    e.preventDefault();

    try {
      // Check if card is initialized
      if (!cardRef.current) {
        console.error('Card payment form not initialized');
        onPaymentError?.('Payment form not ready. Please refresh the page.');
        return;
      }

      const result = await cardRef.current.tokenize();
      console.log('Tokenization result:', result);

      if (result.status === 'OK') {
        onPaymentSuccess(result);
      } else {
        let errorMessage = 'Payment failed';
        if (result.errors) {
          console.error('Tokenization errors:', result.errors);
          errorMessage = result.errors.map(error => error.message).join(', ');
        }
        onPaymentError?.(errorMessage);
      }
    } catch (error) {
      console.error('Payment error details:', error);
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      onPaymentError?.(`Payment processing failed: ${error.message || 'Unknown error'}`);
    }
  };

  const handleApplePayment = async () => {
    try {
      if (!applePayRef.current) {
        console.error('Apple Pay not initialized');
        onPaymentError?.('Apple Pay not available');
        return;
      }

      const result = await applePayRef.current.tokenize();
      console.log('Apple Pay tokenization result:', result);

      if (result.status === 'OK') {
        onPaymentSuccess(result);
      } else {
        let errorMessage = 'Apple Pay failed';
        if (result.errors) {
          console.error('Apple Pay errors:', result.errors);
          errorMessage = result.errors.map(error => error.message).join(', ');
        }
        onPaymentError?.(errorMessage);
      }
    } catch (error) {
      console.error('Apple Pay error:', error);
      onPaymentError?.(`Apple Pay failed: ${error.message || 'Unknown error'}`);
    }
  };

  const handleGooglePayment = async () => {
    try {
      if (!googlePayRef.current) {
        console.error('Google Pay not initialized');
        onPaymentError?.('Google Pay not available');
        return;
      }

      const result = await googlePayRef.current.tokenize();
      console.log('Google Pay tokenization result:', result);

      if (result.status === 'OK') {
        onPaymentSuccess(result);
      } else {
        let errorMessage = 'Google Pay failed';
        if (result.errors) {
          console.error('Google Pay errors:', result.errors);
          errorMessage = result.errors.map(error => error.message).join(', ');
        }
        onPaymentError?.(errorMessage);
      }
    } catch (error) {
      console.error('Google Pay error:', error);
      onPaymentError?.(`Google Pay failed: ${error.message || 'Unknown error'}`);
    }
  };

  return (
    <div>
      {/* Active payment buttons */}
      {(applePayAvailable || googlePayAvailable) && (
        <div style={{ marginBottom: '20px' }}>
          {applePayAvailable && (
            <button
              type="button"
              onClick={handleApplePayment}
              className="apple-pay-button"
              style={{
                width: '100%',
                height: '48px',
                backgroundColor: '#000',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: googlePayAvailable ? '12px' : '0'
              }}
            >
              <span style={{ fontSize: '20px' }}>&#63743;</span> Pay with Apple Pay
            </button>
          )}
          {googlePayAvailable && (
            <div
              id="google-pay-button"
              style={{
                width: '100%',
                minHeight: '48px'
              }}
            ></div>
          )}
          <div style={{
            textAlign: 'center',
            margin: '15px 0',
            color: '#666',
            fontSize: '14px'
          }}>
            or pay with card
          </div>
        </div>
      )}
      <form onSubmit={handleCardPayment}>
        <div id="card-container" style={{ marginBottom: '20px' }}></div>
        <button type="submit" className="submit-button">
          Pay ${price.toFixed(2)}
        </button>
      </form>
    </div>
  );
};

export default SquarePayment;
